import { Fragment, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { Search as SearchIcon, CornerDownLeft, Folder } from 'lucide-react'
import type { SearchSuggestion } from '../../types'

interface SearchSuggestionsDropdownProps {
  suggestions: SearchSuggestion[]
  query: string
  activeIndex: number
  onSelect: (suggestion: SearchSuggestion) => void
  onHoverIndex: (index: number) => void
  isLoading: boolean
}

/**
 * Floating dropdown rendered beneath the search input. Listbox semantics
 * for keyboard a11y — the parent input owns focus and forwards arrow /
 * Enter / Esc handling. Highlights the typed prefix portion of each
 * suggestion so the eye can quickly confirm the match.
 *
 * The user's literal typed query is always rendered as the first option
 * (unless one of the suggestions is already an exact match) so they can
 * commit their own wording when the suggestions don't fit.
 */
export function SearchSuggestionsDropdown({
  suggestions,
  query,
  activeIndex,
  onSelect,
  onHoverIndex,
  isLoading,
}: SearchSuggestionsDropdownProps) {
  const listRef = useRef<HTMLUListElement>(null)

  const trimmedQuery = query.trim()
  // Must mirror Search.tsx's showLiteralQueryOption exactly, or the
  // keyboard-nav index math (which lives in the parent) drifts out of sync
  // with what the dropdown renders. Only a NON-category exact match hides
  // the literal option — a category exact match runs a different
  // (scoped) search, so the broad "Search for X" option stays visible.
  const includesQueryAsExactMatch = useMemo(
    () =>
      suggestions.some(
        (suggestion) =>
          suggestion.type !== 'category' &&
          suggestion.text.toLowerCase() === trimmedQuery.toLowerCase(),
      ),
    [suggestions, trimmedQuery],
  )
  const showLiteralQueryOption = trimmedQuery.length > 0 && !includesQueryAsExactMatch

  useEffect(() => {
    const list = listRef.current
    if (!list) return
    const activeItem = list.querySelector<HTMLLIElement>(
      `[data-suggestion-index="${activeIndex}"]`,
    )
    if (activeItem) {
      activeItem.scrollIntoView({ block: 'nearest' })
    }
  }, [activeIndex])

  const showEmptyShell = isLoading && suggestions.length === 0
  if (!showEmptyShell && suggestions.length === 0 && !showLiteralQueryOption) {
    return null
  }

  return (
    <div
      className="absolute left-0 right-0 top-full mt-2 z-30 rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden"
      role="presentation"
    >
      {showEmptyShell ? (
        <div className="px-4 py-3 text-sm text-gray-500">Loading…</div>
      ) : (
        <ul
          ref={listRef}
          role="listbox"
          aria-label="Search suggestions"
          className="max-h-80 overflow-y-auto py-1"
        >
          {showLiteralQueryOption && (
            <li
              role="option"
              aria-selected={activeIndex === 0}
              data-suggestion-index={0}
              onMouseEnter={() => onHoverIndex(0)}
              onMouseDown={(event) => {
                event.preventDefault()
                onSelect({ text: trimmedQuery, type: 'keyword', categoryId: null })
              }}
              className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer border-b border-gray-100 text-xs sm:gap-3 sm:px-4 sm:py-2.5 sm:text-sm ${
                activeIndex === 0 ? 'bg-gray-100' : 'bg-white'
              }`}
            >
              <SearchIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="min-w-0 flex-1 line-clamp-2 sm:line-clamp-1">
                Search for{' '}
                <span className="font-semibold text-gray-900">
                  "{trimmedQuery}"
                </span>
              </span>
              <CornerDownLeft className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            </li>
          )}
          {suggestions.map((suggestion, idx) => {
            const itemIndex = showLiteralQueryOption ? idx + 1 : idx
            const isActive = itemIndex === activeIndex
            // Suggestions arrive grouped: categories first, then products.
            // Render section headings as presentational rows only (no
            // role="option", no index) so keyboard navigation remains aligned.
            const isFirstCategory =
              suggestion.type === 'category' &&
              (idx === 0 || suggestions[idx - 1].type !== 'category')
            const isFirstProduct =
              suggestion.type !== 'category' &&
              (idx === 0 || suggestions[idx - 1].type === 'category')
            return (
              <Fragment key={`${suggestion.text}-${itemIndex}`}>
                {isFirstCategory && (
                  <li
                    role="presentation"
                    className="border-t border-gray-100 px-4 pt-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400"
                  >
                    Suggested categories
                  </li>
                )}
                {isFirstProduct && (
                  <li
                    role="presentation"
                    className="border-t border-gray-100 px-4 pt-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400"
                  >
                    Suggested products
                  </li>
                )}
                <li
                  role="option"
                  aria-selected={isActive}
                  data-suggestion-index={itemIndex}
                  onMouseEnter={() => onHoverIndex(itemIndex)}
                  onMouseDown={(event) => {
                    // mousedown not click — fires before the input's blur,
                    // so the focus-stealing blur doesn't tear down the
                    // dropdown before the selection registers.
                    event.preventDefault()
                    onSelect(suggestion)
                  }}
                  className={`flex items-start gap-2.5 px-3 py-2 cursor-pointer text-xs sm:gap-3 sm:px-4 sm:py-2.5 sm:text-sm ${
                    isActive ? 'bg-gray-100' : 'bg-white'
                  }`}
                >
                  {suggestion.type === 'category' ? (
                    <Folder className="mt-0.5 w-4 h-4 text-gray-400 flex-shrink-0 sm:mt-0" />
                  ) : (
                    <SearchIcon className="mt-0.5 w-4 h-4 text-gray-400 flex-shrink-0 sm:mt-0" />
                  )}
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-3">
                    <span className="min-w-0 line-clamp-2 leading-5 sm:line-clamp-1">
                      {highlightMatch(suggestion.text, trimmedQuery)}
                    </span>
                    {suggestion.type === 'category' && suggestion.path && (
                      // Full breadcrumb path beside the leaf name on larger screens,
                      // stacked below it on mobile so it does not crush the label.
                      <span className="min-w-0 line-clamp-1 text-[11px] leading-4 text-gray-400 sm:ml-auto sm:text-xs">
                        {suggestion.path}
                      </span>
                    )}
                  </span>
                </li>
              </Fragment>
            )
          })}
        </ul>
      )}
    </div>
  )
}

/**
 * Returns the suggestion with each matching query token wrapped in
 * <strong>. Each typed word is matched independently at word boundaries
 * so multi-word queries like "lever valve" highlight both "Lever" and
 * "Valve" inside "Lever Ball Valve". The final token is treated as a
 * prefix (the user may still be typing) and highlights the whole word
 * it starts, so "lever val" highlights "Lever" + "Valve" in full.
 *
 * Case-insensitive but preserves the original casing of the suggestion.
 */
function highlightMatch(suggestion: string, query: string) {
  if (!query) return suggestion

  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return suggestion

  const lowerSuggestion = suggestion.toLowerCase()
  const lastTokenIndex = tokens.length - 1
  const ranges: Array<{ start: number; end: number }> = []

  tokens.forEach((token, idx) => {
    const escaped = escapeRegex(token)
    // Leading tokens require an exact word match (the user finished
    // typing them). The last token is a prefix that highlights the
    // whole word it starts so a half-typed "val" lights up "Valve".
    const pattern =
      idx === lastTokenIndex
        ? new RegExp(`\\b${escaped}\\w*`, 'g')
        : new RegExp(`\\b${escaped}\\b`, 'g')

    let match: RegExpExecArray | null
    while ((match = pattern.exec(lowerSuggestion)) !== null) {
      if (match[0].length === 0) {
        // Defensive — empty match would infinite-loop exec().
        pattern.lastIndex += 1
        continue
      }
      ranges.push({ start: match.index, end: match.index + match[0].length })
    }
  })

  if (ranges.length === 0) return suggestion

  // Merge overlapping / adjacent ranges so a token that prefix-matches
  // a word also containing another fully-typed token doesn't produce
  // nested <strong> tags.
  ranges.sort((a, b) => a.start - b.start)
  const merged: Array<{ start: number; end: number }> = [ranges[0]]
  for (let idx = 1; idx < ranges.length; idx++) {
    const last = merged[merged.length - 1]
    if (ranges[idx].start <= last.end) {
      last.end = Math.max(last.end, ranges[idx].end)
    } else {
      merged.push(ranges[idx])
    }
  }

  const parts: ReactNode[] = []
  let cursor = 0
  merged.forEach((range, idx) => {
    if (range.start > cursor) {
      parts.push(suggestion.slice(cursor, range.start))
    }
    parts.push(
      <strong key={idx} className="font-semibold text-gray-900">
        {suggestion.slice(range.start, range.end)}
      </strong>,
    )
    cursor = range.end
  })
  if (cursor < suggestion.length) {
    parts.push(suggestion.slice(cursor))
  }

  return <>{parts}</>
}

function escapeRegex(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
